import React, { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, AreaChart, Area, XAxis, YAxis, CartesianGrid } from "recharts";

function calcEMI(p, annualRate, years) {
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (r === 0) return p / n;
  return (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function buildSchedule(p, annualRate, years) {
  const r = annualRate / 12 / 100;
  const emi = calcEMI(p, annualRate, years);
  let balance = p;
  const sched = [];
  for (let y = 1; y <= years; y++) {
    let yp = 0, yi = 0;
    for (let m = 0; m < 12; m++) {
      const interest = balance * r;
      const principal = emi - interest;
      balance -= principal;
      yp += principal; yi += interest;
    }
    sched.push({ year: `Y${y}`, principal: Math.round(yp), interest: Math.round(yi), balance: Math.round(Math.max(balance, 0)) });
  }
  return { emi, sched };
}

function formatINR(n) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString()}`;
}

export default function EmiCalculator() {
  const [principal, setPrincipal] = useState(5000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, sched } = useMemo(() => buildSchedule(principal, rate, years), [principal, rate, years]);
  const total = emi * years * 12;
  const interest = total - principal;

  const pieData = [
    { name: "Principal", value: Math.round(principal) },
    { name: "Interest", value: Math.round(interest) },
  ];

  return (
    <div data-testid="emi-page" className="min-h-screen pb-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <div className="overline mb-3">Financial Tools</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900">EMI Calculator</h1>
        <p className="text-slate-600 mt-3 max-w-2xl font-light">Run scenarios. See your true monthly outflow and lifetime cost of borrowing.</p>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16 grid lg:grid-cols-12 gap-10">
        {/* Inputs */}
        <div className="lg:col-span-5 border border-slate-200 p-8">
          <div className="space-y-10">
            {[
              { label: "Loan Amount", value: principal, set: setPrincipal, min: 500000, max: 100000000, step: 100000, format: formatINR, testId: "emi-principal" },
              { label: "Interest Rate (% p.a.)", value: rate, set: setRate, min: 4, max: 20, step: 0.05, format: (v) => `${v.toFixed(2)}%`, testId: "emi-rate" },
              { label: "Tenure (years)", value: years, set: setYears, min: 1, max: 30, step: 1, format: (v) => `${v} yr`, testId: "emi-years" },
            ].map((s) => (
              <div key={s.label}>
                <div className="flex items-center justify-between mb-3">
                  <div className="overline">{s.label}</div>
                  <div className="font-serif text-xl text-[#D4A437]" data-testid={`${s.testId}-value`}>{s.format(s.value)}</div>
                </div>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={s.value}
                  onChange={(e) => s.set(parseFloat(e.target.value))}
                  data-testid={s.testId}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] text-slate-600 mt-1 tracking-wider">
                  <span>{s.format(s.min)}</span>
                  <span>{s.format(s.max)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Result + chart */}
        <div className="lg:col-span-7 space-y-8">
          <div className="border border-amber-200 bg-gradient-to-br from-slate-900 to-slate-950 p-10">
            <div className="overline mb-2">Monthly EMI</div>
            <div className="font-serif text-6xl text-[#D4A437]" data-testid="emi-result">{formatINR(emi)}</div>
            <div className="grid grid-cols-3 gap-px bg-slate-100 mt-8 border border-slate-100">
              <div className="bg-white p-5"><div className="overline mb-1">Principal</div><div className="font-serif text-xl text-slate-900">{formatINR(principal)}</div></div>
              <div className="bg-white p-5"><div className="overline mb-1">Interest</div><div className="font-serif text-xl text-slate-900">{formatINR(interest)}</div></div>
              <div className="bg-white p-5"><div className="overline mb-1">Total</div><div className="font-serif text-xl text-slate-900">{formatINR(total)}</div></div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="border border-slate-200 p-6">
              <div className="overline mb-4">Principal vs Interest</div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} dataKey="value" innerRadius={60} outerRadius={90} strokeWidth={0}>
                      <Cell fill="#d97706" />
                      <Cell fill="#1e293b" />
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: "#020617", border: "1px solid #334155", color: "#f8fafc" }}
                      formatter={(v) => formatINR(v)}
                    />
                    <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="border border-slate-200 p-6">
              <div className="overline mb-4">Yearly Outstanding Balance</div>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={sched}>
                    <CartesianGrid stroke="#1e293b" vertical={false} />
                    <XAxis dataKey="year" stroke="#64748b" tick={{ fill: "#64748b", fontSize: 11 }} />
                    <YAxis stroke="#64748b" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} />
                    <Tooltip
                      contentStyle={{ background: "#020617", border: "1px solid #334155", color: "#f8fafc" }}
                      formatter={(v) => formatINR(v)}
                    />
                    <Area type="monotone" dataKey="balance" stroke="#d97706" fill="#d97706" fillOpacity={0.2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
